import { useEffect } from "react";
import { Logo, TemplateTextarea, TemplateInput } from "@components";
import { useWatch } from "react-hook-form";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import AmountTokenSelector from "./AmountTokenSelector";

const actionTitleDefaults = {
  Overdue: "Overdue invoices",
  OverdueAndDueThisMonth: "Overdue + Due invoices",
  All: "All invoices",
  None: "Account balance",
  Custom: "Amount due",
};

const EmailEditor = ({ control, errors, merchant, setValue, titleRef, mainTextRef }) => {
  const attachPdf = useWatch({ control, name: "attachPdfStatement" });
  const invoicesRequested = useWatch({ control, name: "invoicesRequested" });

  useEffect(() => {
    if (
      setValue &&
      invoicesRequested &&
      actionTitleDefaults[invoicesRequested]
    ) {
      setValue("emailActionTitle", actionTitleDefaults[invoicesRequested]);
    }
  }, [invoicesRequested, setValue]);

  const primaryColour = merchant?.customisationSettings?.colourSet?.primary;
  const buttonTextColour =
    merchant?.customisationSettings?.colourSet?.buttonText;
  const merchantName = merchant?.name ?? "Your company";

  return (
    <div className="w-full flex flex-col items-center pb-8 pt-6 px-6">
      <div
        className="pt-6 rounded-3xl z-10 flex flex-col items-stretch w-full bg-white"
        style={{ boxShadow: "4px 4px 16px 0px rgba(0,0,0,0.16)" }}
      >
        <div className="px-6 border-b border-zinc-200 py-4">
          <div className="text-xs space-y-1 pb-2 px-2">
            <div className="flex items-center px-2 py-1 cursor-default">
              <span className="w-20 shrink-0 text-zinc-400">From:</span>
              <span className="text-zinc-400">{merchantName}</span>
            </div>
            <div className="flex items-center px-2 py-1 cursor-default">
              <span className="w-20 shrink-0 text-zinc-400">To:</span>
              <span className="text-zinc-400">example@example.com</span>
            </div>
            <div className="flex items-center px-2 py-1 cursor-default">
              <span className="w-20 shrink-0 text-zinc-400">Subject:</span>
              <div className="flex-1 min-w-0 text-zinc-800 font-bold">
                <TemplateInput
                  name="emailSubject"
                  control={control}
                  displayName="Subject"
                  placeholder="e.g. You have outstanding invoices"
                  invalid={!!errors?.emailSubject}
                  borderless
                  maxLength={150}
                  className="text-xs"
                />
              </div>
            </div>
          </div>
          {attachPdf && (
            <div className="inline-flex items-center gap-2 mx-2 px-4 py-2 cursor-default bg-zinc-50 rounded-md border border-zinc-200">
              <PaperClipIcon className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="text-sm text-zinc-500 select-none">
                PDF statement attached
              </span>
            </div>
          )}
        </div>
        <div className="px-6">
          <div className="text-center">
            <div className="flex justify-center p-3 pt-7">
              <Logo
                type="center"
                brand={merchant?.organisationId}
                height="96px"
                width="200px"
              />
            </div>
          </div>
          <div className="text-center cursor-default mt-4 font-bold text-4xl">
            <TemplateInput
              name="emailTitle"
              control={control}
              displayName="Email title"
              placeholder="e.g. Payment due"
              invalid={!!errors?.emailTitle}
              borderless
              wrap
              maxLength={100}
              className="text-center text-2xl"
              rich
              innerRef={titleRef}
            />
          </div>
          <div className="px-6 my-8">
            <TemplateTextarea
              name="emailMainText"
              control={control}
              displayName="Email body"
              placeholder="e.g. Hi {CustomerName}, this is a reminder about your outstanding invoices."
              invalid={!!errors?.emailMainText}
              borderless
              maxLength={2000}
              className="text-xs"
              rich
              innerRef={mainTextRef}
            />
          </div>
          <div className="flex flex-col bg-zinc-100 rounded-2xl cursor-default p-6">
            <div>
              <div className="pb-4">
                <TemplateInput
                  name="emailActionTitle"
                  control={control}
                  displayName="Action area title"
                  placeholder="e.g. Overdue invoices"
                  borderless
                  maxLength={100}
                  className="text-md"
                />
              </div>
              <div className="pb-6">
                <AmountTokenSelector
                  invoicesRequested={invoicesRequested}
                  onChange={(key) => {
                    setValue("invoicesRequested", key);
                    if (key === "None") setValue("mandatory", false);
                    if (key === "Custom") setValue("mandatory", true);
                  }}
                />
              </div>
            </div>
            <div className="flex justify-center w-full">
              <div
                className="w-full rounded-lg py-2 text-center font-medium"
                style={{
                  backgroundColor: primaryColour,
                  color: buttonTextColour,
                }}
              >
                <TemplateInput
                  name="emailButtonText"
                  control={control}
                  displayName="Button text"
                  placeholder="e.g. Review and pay"
                  borderless
                  className="text-center font-medium"
                  style={{ color: buttonTextColour }}
                  maxLength={22}
                />
              </div>
            </div>
          </div>
          <div className="my-6">
            <div className="justify-center flex items-end">
              <p className="text-xs text-zinc-700 font-medium cursor-default mb-8">
                Powered by <strong>Ontik</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailEditor;
